import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { SearchResultItem } from '../../models/global-search.model';

type FilterType = 'all' | 'document_model' | 'field';

interface FilterOption {
  type: FilterType;
  label: string;
  count: number;
}

@Component({
  selector: 'app-search-results-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './search-results-dropdown.component.html',
  styleUrl: './search-results-dropdown.component.scss'
})
export class SearchResultsDropdownComponent implements OnChanges {
  @Input() results: SearchResultItem[] = [];
  @Input() isSearching: boolean = false;
  @Input() searchTerm: string = '';
  @Output() onClose = new EventEmitter<void>();
  // Emitted when a result is clicked and we're navigating away, as opposed to
  // the dropdown being dismissed (onClose) - the parent keeps the search
  // state for this so it can be restored without a new API call on return.
  @Output() resultSelected = new EventEmitter<void>();

  // Filter state
  selectedFilter: FilterType = 'all';
  filterOptions: FilterOption[] = [
    { type: 'all', label: 'All', count: 0 },
    { type: 'document_model', label: 'Document Model', count: 0 },
    { type: 'field', label: 'Fields', count: 0 },
  ];

  // Group results by type for display
  documentModelResults: SearchResultItem[] = [];
  fieldResults: SearchResultItem[] = [];

  // Filtered results based on selected filter
  filteredResults: SearchResultItem[] = [];

  constructor(private router: Router) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results'] || changes['searchTerm']) {
      this.groupResultsByType();
      this.updateFilterCounts();
      this.applyFilter();
    }
  }

  private groupResultsByType(): void {
    this.documentModelResults = this.results.filter(r => r.type === 'document_model');
    this.fieldResults = this.results.filter(r => r.type === 'field');
  }

  private updateFilterCounts(): void {
    this.filterOptions[0].count = this.results.length; // All
    this.filterOptions[1].count = this.documentModelResults.length; // Document Model
    this.filterOptions[2].count = this.fieldResults.length; // Fields
  }

  selectFilter(filterType: FilterType): void {
    this.selectedFilter = filterType;
    this.applyFilter();
  }

  private applyFilter(): void {
    switch (this.selectedFilter) {
      case 'document_model':
        this.filteredResults = this.documentModelResults;
        break;
      case 'field':
        this.filteredResults = this.fieldResults;
        break;
      case 'all':
      default:
        // For 'all' filter, use backend's ordering (by matched score)
        this.filteredResults = this.results;
    }
  }

  navigateToEditDocumentModel(documentModel: string, fieldName?: string): void {
    this.resultSelected.emit();
    const navigationExtras = fieldName ? { queryParams: { search: fieldName } } : {};
    this.router.navigate(['/edit-document-model', documentModel], navigationExtras);
  }

  close(): void {
    this.onClose.emit();
  }

  getPageName(type: string): string {
    switch (type) {
      case 'field':
        return 'Edit Document Model Page';
      default:
        return '';
    }
  }

  isFilterActive(filterType: FilterType): boolean {
    return this.selectedFilter === filterType;
  }
}
